// src/components/RegistrationFlow.jsx
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getImagePath } from '../utils/imageAssets';
import './RegistrationFlow.css';

const RegistrationFlow = () => {
  const [step, setStep] = useState('category');
  const [userName, setUserName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedPhotoType, setSelectedPhotoType] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const navigate = useNavigate();
  
  const categories = [
    { id: 'sports', name: 'Sports', icon: '🏆' },
    { id: 'corporate', name: 'Corporate', icon: '🏢' },
    { id: 'personal', name: 'Personal', icon: '👤' }
  ];
  
  const subcategories = {
    sports: [
      { id: 'cheer', name: 'Cheer', icon: '📣' },
      { id: 'dance', name: 'Dance', icon: '💃' },
      { id: 'ice-hockey', name: 'Ice Hockey', icon: '🏒' },
      { id: 'equestrian', name: 'Equestrian', icon: '🐎' },
      { id: 'gymnastics', name: 'Gymnastics', icon: '🤸' },
      { id: 'lacrosse', name: 'Lacrosse', icon: '🥍' },
      { id: 'tennis', name: 'Tennis', icon: '🎾' },
      { id: 'soccer', name: 'Soccer', icon: '⚽' },
      { id: 'baseball', name: 'Baseball/Softball', icon: '⚾' },
      { id: 'basketball', name: 'Basketball', icon: '🏀' },
      { id: 'wrestling', name: 'Wrestling', icon: '🤼' },
      { id: 'football', name: 'Football', icon: '🏈' },
      { id: 'profile', name: 'Profile', icon: '📸' }
    ],
    corporate: [
      { id: 'groups', name: 'Groups', icon: '👥' },
      { id: 'artistic', name: 'Artistic', icon: '🎨' },
      { id: 'profile', name: 'Profile', icon: '📸' }
    ],
    personal: [
      { id: 'groups', name: 'Groups', icon: '👪' },
      { id: 'artistic', name: 'Artistic', icon: '🖼️' },
      { id: 'profile', name: 'Profile', icon: '📱' }
    ]
  };

  const photoTypes = [
    { id: 'team-branded', name: 'Team Branded', preview: 'team-branded' },
    { id: 'team-branded-group', name: 'Team Branded (Group)', preview: 'team-branded-group' },
    { id: 'individual', name: 'Individual', preview: 'individual' },
    { id: 'small-group', name: 'Group (2-5)', preview: 'small-group' },
    { id: 'team', name: 'Team', preview: 'team' },
    { id: 'collage', name: 'Collage', preview: 'collage' }
  ];
  
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setStep('name');
  };
  
  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (userName.trim()) {
      setStep('subcategory');
    }
  };
  
  const handleSubcategorySelect = (subcategoryId) => {
    if (selectedSubcategories.includes(subcategoryId)) {
      setSelectedSubcategories(selectedSubcategories.filter(id => id !== subcategoryId));
    } else {
      // Make selection exclusive - only one subcategory can be selected
      setSelectedSubcategories([subcategoryId]);
      setStep('phototype');
    }
  };

  const handlePhotoTypeSelect = (photoTypeId) => {
    setSelectedPhotoType(photoTypeId);
  };
  
  const handleContinue = () => {
    // Here we can save the user preferences before redirecting
    console.log('Selected category:', selectedCategory);
    console.log('Selected subcategory:', selectedSubcategories[0]);
    console.log('Selected photo type:', selectedPhotoType);
    console.log('User name:', userName);
    
    // Navigate to editor
    navigate("/editor");
  };

  const handleBackToSubcategories = () => {
    setSelectedPhotoType(null);
    setStep('subcategory');
  };

  const handleShowPreview = (previewId) => {
    // Get the image path from our utility
    const imagePath = getImagePath(previewId);
    console.log("Setting preview image:", imagePath);
    setPreviewImage(imagePath);
  };

  const handleHidePreview = () => {
    setPreviewImage(null);
  };
  
  // Render different steps of the registration flow
  const renderStep = () => {
    switch(step) {
      case 'category':
        return (
          <div className="registration-step category-step">
            <h1 className="registration-heading">What are you interested in?</h1>
            <div className="category-grid">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="category-item"
                  onClick={() => handleCategorySelect(category.id)}
                >
                  <div className="category-card">
                    <div className="icon-container">
                      <span className="category-icon">{category.icon}</span>
                    </div>
                    <h2 className="category-name">{category.name}</h2>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
        
      case 'name':
        return (
          <div className="registration-step name-step">
            <h1 className="registration-heading">What's your name?</h1>
            <form onSubmit={handleNameSubmit} className="name-form">
              <div className="input-container">
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Enter your name"
                  className="name-input"
                  required
                />
              </div>
              <button
                type="submit"
                className="continue-button"
              >
                Continue
              </button>
            </form>
          </div>
        );
        
      case 'subcategory':
        return (
          <div className="registration-step subcategory-step">
            <div className="header-with-back">
              <button 
                onClick={() => setStep('name')}
                className="back-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="back-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back
              </button>
              <h1 className="welcome-heading">Welcome {userName}</h1>
            </div>
            
            <h2 className="subcategory-instruction">
              Select your {categories.find(c => c.id === selectedCategory)?.name} interest
            </h2>
            
            <div className="subcategory-grid">
              {subcategories[selectedCategory].map((subcategory) => (
                <div
                  key={subcategory.id}
                  className={`subcategory-item ${selectedSubcategories.includes(subcategory.id) ? 'selected' : ''}`}
                  onClick={() => handleSubcategorySelect(subcategory.id)}
                >
                  <div 
                    className={`subcategory-card ${selectedSubcategories.includes(subcategory.id) ? 'selected' : ''}`}
                  >
                    <div className={`subcategory-icon-container ${selectedSubcategories.includes(subcategory.id) ? 'selected' : ''}`}>
                      <span className={`subcategory-icon ${selectedSubcategories.includes(subcategory.id) ? 'selected' : ''}`}>
                        {subcategory.icon}
                      </span>
                    </div>
                    <h3 className="subcategory-name">{subcategory.name}</h3>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'phototype':
        return (
          <div className="registration-step phototype-step">
            <div className="header-with-back">
              <button 
                onClick={handleBackToSubcategories}
                className="back-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="back-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Back
              </button>
              <h1 className="welcome-heading">Photo Type Selection</h1>
            </div>
            
            <h2 className="subcategory-instruction">
              Select the type of photos for {subcategories[selectedCategory].find(s => s.id === selectedSubcategories[0])?.name}
            </h2>
            
            <div className="phototype-grid">
              {photoTypes.map((photoType) => (
                <div
                  key={photoType.id}
                  className={`phototype-item ${selectedPhotoType === photoType.id ? 'selected' : ''}`}
                  onClick={() => handlePhotoTypeSelect(photoType.id)}
                  onMouseEnter={() => handleShowPreview(photoType.preview)}
                  onMouseLeave={handleHidePreview}
                >
                  <div 
                    className={`phototype-card ${selectedPhotoType === photoType.id ? 'selected' : ''}`}
                  >
                    <h3 className="phototype-name">{photoType.name}</h3>
                  </div>
                </div>
              ))}
            </div>

            {previewImage && (
              <div className="preview-container">
                <img src={previewImage} alt="Preview" className="preview-image" />
              </div>
            )}
            
            <div className="button-container">
              <button
                className={`continue-button ${!selectedPhotoType ? 'disabled' : ''}`}
                onClick={handleContinue}
                disabled={!selectedPhotoType}
              >
                Continue
              </button>
              
              <Link to="/editor" className="editor-link">
                Go To Editor
              </Link>
            </div>
          </div>
        );
        
      default:
        return <div>Unknown step</div>;
    }
  };
  
  return (
    <div className="registration-container">
      <div className="registration-card">
        <div className="registration-content">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default RegistrationFlow;